import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { Account } from '../../interfaces/accounting.interface';
import { InterSegmentSettings as Settings } from '../../interfaces/interSegment.interface';
import { InterSegmentService } from '../../services/interSegment.service';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { leafAccounts } from '../../utils/postingAccounts';

type Slot = 'dueFromAccountId' | 'dueToAccountId' | 'interestIncomeAccountId' | 'interestExpenseAccountId' | 'defaultCashAccountId';

/**
 * Account pickers for inter-segment loans. One due-from and one due-to account give every pairwise
 * balance (the counterparty segment lives on the journal line). "Create suggested accounts" adds only
 * the accounts that do not exist yet and fills empty pickers; it never edits what is configured.
 */
const InterSegmentSettings: React.FC = () => {
    const toast = useToast();
    const canManage = usePermission(PERMISSIONS.SEGMENT_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [form, setForm] = useState<Settings>({ institutionId });
    const [note, setNote] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [settings, loaded] = await Promise.all([
                InterSegmentService.getSettings(institutionId),
                AccountService.getAllAccounts(),
            ]);
            setForm({ ...settings, institutionId });
            setAccounts(leafAccounts(loaded ?? []));
        } catch (e) {
            toast.showApiError(e, 'Failed to load inter-segment settings');
        } finally {
            setLoading(false);
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const set = (slot: Slot) => (id: string) => setForm(f => ({ ...f, [slot]: id || null }));
    const ofClass = (pc: string) => accounts.filter(a => a.postingClass === pc);
    const ofType = (t: string) => accounts.filter(a => a.accountType === t);

    const save = async () => {
        setSaving(true);
        try {
            setForm(await InterSegmentService.saveSettings({ ...form, institutionId }));
            toast.showSuccess('Settings saved');
        } catch (e) {
            toast.showApiError(e, 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const suggest = async () => {
        setSaving(true);
        try {
            const res = await InterSegmentService.createSuggestedAccounts(institutionId);
            setNote(`Created ${res.created.length} account(s)${res.created.length ? ` (${res.created.join(', ')})` : ''}; `
                + `${res.alreadyPresent.length} already existed${res.skipped.length ? `; skipped: ${res.skipped.join('; ')}` : ''}.`);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to create suggested accounts');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
    }

    const field = (slot: Slot, label: string, options: Account[], tag: Record<string, string>, help?: string) => (
        <Box sx={{ flex: '1 1 320px', maxWidth: 520 }}>
            <AccountAutocomplete accounts={options} value={form[slot] ?? ''} onChange={set(slot)} label={label}
                disabled={!canManage} placeholder="None" helperText={help} {...tag} />
        </Box>
    );

    return (
        <Box data-help-page="accounting.interSegSettings.page" sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">Inter-segment settings</Typography>
            {!canManage && <Alert severity="warning">You have read-only access.</Alert>}
            <Typography variant="body2" color="text.secondary">
                Loans between segments post a due-from line in the lender segment and a due-to line in the borrower
                segment, each carrying the other segment as counterparty, so every segment balances on its own.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {field('dueFromAccountId', 'Due from segments (asset)', ofClass('INTER_SEGMENT_RECEIVABLE'), { 'data-help': 'accounting.interSegSettings.field.dueFrom' },
                    'Posting class must be INTER_SEGMENT_RECEIVABLE')}
                {field('dueToAccountId', 'Due to segments (liability)', ofClass('INTER_SEGMENT_PAYABLE'), { 'data-help': 'accounting.interSegSettings.field.dueTo' },
                    'Posting class must be INTER_SEGMENT_PAYABLE')}
                {field('interestIncomeAccountId', 'Inter-segment interest income', ofType('REVENUE'), { 'data-help': 'accounting.interSegSettings.field.interestIncome' })}
                {field('interestExpenseAccountId', 'Inter-segment interest expense', ofType('EXPENSE'), { 'data-help': 'accounting.interSegSettings.field.interestExpense' })}
                {field('defaultCashAccountId', 'Default cash / bank account', ofType('ASSET'), { 'data-help': 'accounting.interSegSettings.field.defaultCash' })}
            </Box>
            {note && <Alert severity="info" onClose={() => setNote(null)}>{note}</Alert>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button data-help="accounting.interSegSettings.save" variant="contained" onClick={save} disabled={!canManage || saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button data-help="accounting.interSegSettings.suggest" variant="outlined" onClick={suggest} disabled={!canManage || saving}>
                    Create suggested accounts
                </Button>
            </Box>
        </Box>
    );
};

export default InterSegmentSettings;
